// GENERAL
import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// API
import {
	fetchOrdersDetails,
	fetchSalesOrderItems,
	getInvoices,
	getShipments,
} from "../../../api/OrdersApi";

// TYPES
import { SalesOrder, ShipToAddress } from "../../../types";

// FIELDS
import {
	SUPERUSERFIELDS_COMPASS,
	ADMINFIELDS_COMPASS,
	MANAGERFIELDS_COMPASS,
	ADMINFIELDS_COMPASSCANADA,
	SUPERUSERFIELDS_COMPASSCANADA,
	MANAGERFIELDS_COMPASSCANADA,
	ADMINFIELDS_QUAD,
	ITSHOPPERFIELDS_QUAD,
	BUYERFIELDS_QUAD,
	DEFAULTFIELDS,
} from "./index";

// HOOKS
import { useDataTableContext, usePageDataContext } from "../../../../../hooks";
import { formatSalesOrderNotes } from "../../../helpers";
import useHostNameStore from "../../../../../stores/useHostNameStore";
import { FormattedSalesOrderNotes } from "../../../types";
import useUserStore from "../../../../../stores/useUserStore";

export const useOrderDetailsViewModel = (): {
	orderDetails: SalesOrder;
	orderAddressDetails: ShipToAddress | null | undefined;
	isLoading: boolean;
	setRefetchData: React.Dispatch<React.SetStateAction<boolean>>;
	refetchData: boolean;
	hasNavigationCompassEditPermission: boolean;
	salesOrderNotes: FormattedSalesOrderNotes | null;
	fields: Record<string, any>;
	salesOrderItems: any | null | undefined;
	contractorInfo?: string | null | undefined;
	isDataFormattingLoading: boolean;
	showErrorMessage: boolean;
	invoiceData: any;
	shipmentsData: any;
} => {
	//get user data from
	const { activeUuid } = useDataTableContext();
	const { pageMeta } = usePageDataContext();
	const queryClient = useQueryClient();
	const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);

	const [refetchData, setRefetchData] = useState<boolean>(false);
	const [
		hasNavigationCompassEditPermission,
		setHasNavigationCompassEditPermission,
	] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isDataFormattingLoading, setIsDataFormattingLoading] =
		useState<boolean>(false);
	const [orderAddressDetails, setOrderAddressDetails] = useState<
		ShipToAddress | null | undefined
	>();
	const [invoiceData, setInvoiceData] = useState<SalesOrder>(
		{} as SalesOrder,
	);
	const [shipmentsData, setShipmentsData] = useState<SalesOrder>(
		{} as SalesOrder,
	);

	const [orderDetails, setOrderDetails] = useState<SalesOrder>(
		{} as SalesOrder,
	);
	const [salesOrderItems, setSalesOrderItems] = useState<
		any | null | undefined
	>();

	const [salesOrderNotes, setSalesOrderNotes] =
		useState<FormattedSalesOrderNotes | null>(null);

	const { hostName } = useHostNameStore();
	const { user } = useUserStore();

	const getFieldsByHostAndPrivilege = () => {
		const fieldMapping: Record<
			string,
			{
				admin?: any;
				superuser?: any;
				manager?: any;
				globalAdmin?: any;
				itShopper?: any;
				buyer?: any;
			}
		> = {
			COMPASS: {
				admin: ADMINFIELDS_COMPASS,
				superuser: SUPERUSERFIELDS_COMPASS,
				manager: MANAGERFIELDS_COMPASS,
			},
			COMPASSCANADA: {
				admin: ADMINFIELDS_COMPASSCANADA,
				superuser: SUPERUSERFIELDS_COMPASSCANADA,
				manager: MANAGERFIELDS_COMPASSCANADA,
			},
			QUAD: {
				globalAdmin: ADMINFIELDS_QUAD,
				itShopper: ITSHOPPERFIELDS_QUAD,
				buyer: BUYERFIELDS_QUAD,
			},
		};

		const hostFields = fieldMapping[hostName];

		// Type guard to ensure user.role is a valid key
		const validRoles = [
			"admin",
			"superuser",
			"manager",
			"globalAdmin",
			"itShopper",
			"buyer",
		] as const;
		const role = user.role as string;

		if (hostFields && validRoles.includes(role as any)) {
			return hostFields[role as keyof typeof hostFields] || DEFAULTFIELDS;
		}

		return DEFAULTFIELDS;
	};

	const fields = getFieldsByHostAndPrivilege();

	const { data: orderDetailsResponse } = useQuery({
		queryKey: ["orderDetailsResponse", activeUuid, user.uuid, refetchData],
		queryFn: () =>
			fetchOrdersDetails(
				activeUuid as string,
				fields.apiFields.fetchOrderDetails,
			),
		enabled:
			!!activeUuid && !!user.uuid && !!fields.apiFields.fetchOrderDetails,
		refetchOnMount: "always",
		staleTime: 0,
	});

	useEffect(() => {
		setIsLoading(true);
		setIsDataFormattingLoading(true);
		const fetchOrderBoxDetails = async () => {
			if (!activeUuid || !pageMeta || !fields || !orderDetailsResponse)
				return;

			try {
				const hasPermissions =
					pageMeta?.acl["sales-orders"][
						"navigation-edit-on-commerce"
					];
				const salesOrderItems = await fetchSalesOrderItems(
					activeUuid,
					fields.apiFields.fetchOrderItems,
				);

				const invoiceData = await getInvoices(activeUuid);

				const shipmentsData = await getShipments(activeUuid);

				setShipmentsData(shipmentsData || ({} as SalesOrder));

				setInvoiceData(invoiceData || ({} as SalesOrder));
				setOrderDetails(orderDetailsResponse);
				setSalesOrderItems(salesOrderItems.items || []);
				setHasNavigationCompassEditPermission(hasPermissions);

				const salesOrderNotes = formatSalesOrderNotes(
					orderDetailsResponse?.salesOrderNotes,
				);

				setSalesOrderNotes(salesOrderNotes);

				setOrderAddressDetails(orderDetailsResponse?.shipToAddress);
			} catch (error) {
				setShowErrorMessage(true);
			} finally {
				setIsLoading(false);
			}
		};

		fetchOrderBoxDetails();
	}, [activeUuid, refetchData, pageMeta, fields, orderDetailsResponse]);

	return {
		orderDetails,
		salesOrderItems,
		orderAddressDetails,
		fields,
		isLoading,
		setRefetchData,
		refetchData,
		hasNavigationCompassEditPermission,
		salesOrderNotes,
		isDataFormattingLoading,
		showErrorMessage,
		invoiceData,
		shipmentsData,
	};
};
